/* Scoped New Arrivals enhancement. Product, price and availability stay server-rendered. */
if (!customElements.get('product-catalog')) {
  customElements.define('product-catalog', class extends HTMLElement {
    connectedCallback() {
      if (this.events || !window.HTMLDialogElement?.prototype.showModal) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.quickDialog = this.querySelector('.product-catalog__dialog--quick');
      this.zoomDialog = this.querySelector('.product-catalog__dialog--zoom');
      this.content = this.querySelector('.product-catalog__content');
      this.status = this.querySelector('.product-catalog__status');
      this.fallback = this.querySelector('.product-catalog__fallback');
      this.openers = new WeakMap();
      this.querySelectorAll('[data-catalog-zoom]').forEach(button => { button.hidden = false; });
      this.querySelectorAll('[data-catalog-quick], [data-catalog-add]').forEach(button => { button.hidden = false; });
      this.querySelectorAll('[data-catalog-fallback]').forEach(link => { link.hidden = true; });
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('keydown', event => {
        const activeDialog = this.zoomDialog.open ? this.zoomDialog : this.quickDialog;
        if (!activeDialog.open) return;
        if (event.key === 'Tab') {
          const controls = Array.from(getFocusableElements(activeDialog)).filter(element =>
            element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
          );
          const target = event.shiftKey ? controls.at(-1) : controls[0];
          if (target && ((event.shiftKey && event.target === controls[0]) || (!event.shiftKey && event.target === controls.at(-1)))) {
            event.preventDefault();
            event.stopPropagation();
            target.focus();
          }
          return;
        }
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        activeDialog.close();
      }, { signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-catalog-variant]')) this.loadProduct(event.target.value);
      }, { signal });
      this.addEventListener('submit', event => {
        if (!this.content.contains(event.target)) return;
        this.submitting = true;
        const select = this.content.querySelector('[data-catalog-variant]');
        if (select) select.disabled = true;
      }, { signal, capture: true });
      [this.quickDialog, this.zoomDialog].forEach(dialog => {
        dialog.addEventListener('close', () => this.onClose(dialog), { signal });
        // Only a gesture that starts AND ends outside the panel dismisses it.
        dialog.addEventListener('pointerdown', event => {
          this.backdropDown = event.target === dialog && this.isOutside(event, dialog);
        }, { signal });
      });
      if (typeof subscribe === 'function') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'product-form' || !this.submitting) return;
          this.submitting = false;
          this.zoomDialog.close();
          this.quickDialog.close();
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          cart?.setActiveElement(this.openers.get(this.quickDialog));
        });
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, () => { this.submitting = false; });
      }
    }

    disconnectedCallback() {
      this.request?.abort();
      this.events?.abort();
      this.events = null;
      this.buttonObserver?.disconnect();
      this.unsubscribeCart?.();
      this.unsubscribeError?.();
      this.quickDialog?.close();
      this.zoomDialog?.close();
      document.body.classList.remove('catalog-dialog-open');
    }

    isOutside(event, dialog) {
      const box = dialog.getBoundingClientRect();
      return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
    }

    onClick(event) {
      const close = event.target.closest('[data-catalog-close]');
      if (close) { close.closest('dialog').close(); return; }
      if (event.target.matches('dialog') && this.backdropDown && this.isOutside(event, event.target)) {
        event.target.close(); return;
      }
      const zoom = event.target.closest('[data-catalog-zoom]');
      if (zoom) {
        const image = this.zoomDialog.querySelector('img');
        image.src = zoom.dataset.image;
        image.alt = zoom.dataset.title;
        this.openDialog(this.zoomDialog, zoom);
        return;
      }
      const thumbnail = event.target.closest('[data-catalog-thumbnail]');
      if (thumbnail) {
        const image = this.content.querySelector('.catalog-preview__image');
        image.removeAttribute('srcset');
        image.src = thumbnail.dataset.image;
        image.closest('[data-catalog-zoom]').dataset.image = thumbnail.dataset.zoomImage;
        this.content.querySelectorAll('[data-catalog-thumbnail]').forEach(button => button.setAttribute('aria-pressed', String(button === thumbnail)));
        return;
      }
      const quick = event.target.closest('[data-catalog-quick], [data-catalog-add]');
      if (!quick || quick.disabled) return;
      event.preventDefault();
      this.productUrl = quick.dataset.productUrl;
      this.purchaseIntent = quick.hasAttribute('data-catalog-add');
      this.quickDialog.setAttribute('aria-label', quick.dataset.title);
      this.fallback.href = this.productUrl;
      this.openDialog(this.quickDialog, quick);
      this.loadProduct();
    }

    openDialog(dialog, opener) {
      this.openers.set(dialog, opener);
      document.body.classList.add('catalog-dialog-open');
      dialog.showModal();
      dialog.querySelector('[data-catalog-close]').focus({ preventScroll: true });
    }

    onClose(dialog) {
      // A queued close event must not clear an immediately reopened dialog.
      if (dialog.open) return;
      if (dialog === this.quickDialog) {
        this.request?.abort();
        this.buttonObserver?.disconnect();
        this.content.replaceChildren();
        this.status.hidden = true;
        this.fallback.hidden = true;
        this.submitting = false;
      }
      if (!this.quickDialog.open && !this.zoomDialog.open) document.body.classList.remove('catalog-dialog-open');
      this.openers.get(dialog)?.focus({ preventScroll: true });
    }

    async loadProduct(variantId) {
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(new Error('Product request timed out')), 15000);
      this.status.hidden = false;
      this.status.textContent = document.documentElement.lang === 'bg' ? 'Зареждане…' : 'Loading…';
      this.fallback.hidden = true;
      this.content.setAttribute('aria-busy', 'true');
      this.content.querySelectorAll('select, [type="submit"]').forEach(input => { input.disabled = true; });
      const url = new URL(this.productUrl, location.origin);
      url.searchParams.set('view', 'catalog-preview');
      if (variantId) url.searchParams.set('variant', variantId);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const preview = html.querySelector('[data-catalog-product]');
        if (!preview) throw new Error('Missing product preview');
        if (controller.signal.aborted || !this.quickDialog.open) return;
        this.buttonObserver?.disconnect();
        this.content.replaceChildren(document.importNode(preview, true));
        this.status.hidden = true;
        // Reuse the theme’s product-form/cart logic, not a second cart implementation.
        const button = this.content.querySelector('[type="submit"]');
        if (button) {
          this.buttonObserver = new MutationObserver(() => {
            const select = this.content.querySelector('[data-catalog-variant]');
            if (select) select.disabled = button.classList.contains('loading');
          });
          this.buttonObserver.observe(button, { attributes: true, attributeFilter: ['class'] });
        }
        if (variantId) this.content.querySelector('[data-catalog-variant]')?.focus({ preventScroll: true });
        else if (this.purchaseIntent) {
          // An Add to cart card action must not silently choose a clothing size.
          const target = this.content.querySelector('[data-catalog-variant]') || button;
          if (target) {
            target.focus({ preventScroll: true });
            const offset = target.getBoundingClientRect().top - this.quickDialog.getBoundingClientRect().top;
            this.quickDialog.scrollTop += offset - this.quickDialog.clientHeight / 2;
          }
        }
      } catch (error) {
        if (this.request !== controller || !this.quickDialog.open || error.name === 'AbortError') return;
        this.content.replaceChildren();
        this.status.textContent = this.dataset.error;
        this.fallback.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) this.content.removeAttribute('aria-busy');
      }
    }
  });
}
